import React, { useEffect, useState } from 'react';
import {
  BOARD_EDITORIAL_LIMITS,
  boardLocalizedIssues,
  type AdminBoardTeamDto,
} from '@living-library/contracts';
import { boardError, json, type BoardRequest } from './board-api';
import { button, c, card, input, LanguageEditor, Requirements } from './board-ui';

export function BoardTeam({ request }: { request: BoardRequest }) {
  const [members, setMembers] = useState<AdminBoardTeamDto[]>([]),
    [query, setQuery] = useState(''),
    [busy, setBusy] = useState(false),
    [error, setError] = useState('');
  async function load(value: string) {
    setBusy(true);
    setError('');
    try {
      setMembers(await request(`/team?query=${encodeURIComponent(value)}`));
    } catch (error) {
      setError(boardError(error));
    } finally {
      setBusy(false);
    }
  }
  useEffect(() => {
    void load('');
  }, [request]);
  async function save(member: AdminBoardTeamDto) {
    setBusy(true);
    setError('');
    try {
      const saved = await request<AdminBoardTeamDto>(
        `/team/${member.id}`,
        json('PUT', { enabled: member.enabled, role: member.role }),
      );
      setMembers((items) => items.map((item) => (item.id === saved.id ? saved : item)));
    } catch (error) {
      setError(boardError(error));
    } finally {
      setBusy(false);
    }
  }
  const change = (member: AdminBoardTeamDto) =>
    setMembers((items) => items.map((item) => (item.id === member.id ? member : item)));
  return (
    <section>
      <h2 className="mb-5 font-serif text-3xl">{c.team}</h2>
      <form
        className={`${card} mb-6 flex flex-wrap items-end gap-3`}
        onSubmit={(event) => {
          event.preventDefault();
          void load(query);
        }}
      >
        <label className="min-w-40 flex-1 text-sm font-semibold">
          {c.account}
          <input
            className={input}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            maxLength={200}
          />
        </label>
        <button className={button} disabled={busy}>
          {c.search}
        </button>
        <button className={button} disabled={busy} type="button" onClick={() => void load('')}>
          {c.team}
        </button>
      </form>
      {error && (
        <p role="alert" className="my-4 text-warning">
          {error}
        </p>
      )}
      {busy && (
        <p className="admin-empty" role="status">
          {c.loading}
        </p>
      )}
      {!busy && !error && !members.length && <p className="admin-empty">{c.empty}</p>}
      <div className="grid gap-4">
        {members.map((member) => {
          const issues = member.enabled
            ? boardLocalizedIssues(member.role, 'role', BOARD_EDITORIAL_LIMITS.role)
            : [];
          return (
            <article key={member.id} className={card}>
              <div className="flex flex-wrap items-center gap-3">
                {member.avatarUrl && (
                  <img
                    src={member.avatarUrl}
                    className="size-12 rounded-full object-cover"
                    alt=""
                  />
                )}
                <div className="min-w-0 flex-1">
                  <h3 className="font-serif text-2xl">{member.displayName || member.id}</h3>
                  <p className="break-all text-xs text-ink-muted">{member.id}</p>
                </div>
                <label className="flex min-h-11 items-center gap-3">
                  <input
                    type="checkbox"
                    className="size-5 accent-thread"
                    checked={member.enabled}
                    disabled={busy}
                    onChange={(event) => change({ ...member, enabled: event.target.checked })}
                  />
                  {c.enabled}
                </label>
              </div>
              <LanguageEditor
                label={c.role}
                value={member.role}
                limit={BOARD_EDITORIAL_LIMITS.role}
                disabled={busy}
                onChange={(role) => change({ ...member, role })}
              />
              <Requirements issues={issues} />
              <button
                className="admin-button-primary"
                disabled={busy || issues.length > 0}
                onClick={() => void save(member)}
              >
                {c.save}
              </button>
            </article>
          );
        })}
      </div>
    </section>
  );
}
